// src/cockpit4/components/HermesMemoryPanel.tsx
// ============================================
// 企业记忆系统（Hermes 助手 P0-功能1）
//
// 功能：
//   1. 记忆列表展示（按类型/分层筛选 + 关键词搜索）
//   2. 添加 / 编辑 / 删除 记忆
//   3. 重要记忆置顶（pinned）
//   4. 记忆统计：总数 / 各类型分布 / 各分层分布
//   5. 知识关联：点击记忆卡片可调用 HermesRuntime 检索企业知识库，
//      展示关联结果（无返回则显示空状态）
//
// 数据来源：
//   - 企业记忆：localStorage（key: xeaos_hermes_memory）持久化
//   - 企业知识库：RuntimeManager.query(RuntimeName.Hermes,
//                  { type: 'memory_search', query: '...' }) 检索
//
// 记忆分层：短期（会话内）/ 中期（最近7天）/ 长期（永久）
// 记忆类型：事实 / 偏好 / 任务 / 关系 / 知识
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Brain,
  Plus,
  Search,
  Pin,
  PinOff,
  Pencil,
  Trash2,
  X,
  Loader2,
  Database,
  Link2,
  Tag,
  Clock,
  Layers,
  Filter,
  Sparkles,
  FileText,
  AlertCircle,
  Inbox,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";

// ============================================
// 类型定义
// ============================================

/** 记忆类型：事实 / 偏好 / 任务 / 关系 / 知识 */
type MemoryType = "fact" | "preference" | "task" | "relation" | "knowledge";

/** 记忆分层：短期（会话内）/ 中期（最近7天）/ 长期（永久） */
type MemoryScope = "short" | "mid" | "long";

/** 单条记忆项 */
interface MemoryItem {
  id: string;
  type: MemoryType;
  scope: MemoryScope;
  title: string;
  content: string;
  tags: string[];
  pinned: boolean;
  createdAt: string;
  updatedAt: string;
  lastAccessedAt?: string;
  accessCount: number;
  source: "auto" | "manual";
}

/** 表单数据（添加 / 编辑共用） */
interface MemoryForm {
  type: MemoryType;
  scope: MemoryScope;
  title: string;
  content: string;
  tagsInput: string; // 逗号分隔的标签字符串
}

/** 知识关联检索结果（结构宽松，兼容多种返回形态） */
interface KnowledgeResult {
  title?: string;
  content?: string;
  snippet?: string;
  path?: string;
  score?: number;
  source?: string;
}

// ============================================
// 常量：标签 / 颜色映射
// ============================================

const STORAGE_KEY = "xeaos_hermes_memory";

/** 记忆类型 → 中文标签 + 主题色（紫色系为主色调） */
const TYPE_META: Record<MemoryType, { label: string; color: string }> = {
  fact: { label: "事实", color: "#A78BFA" },       // violet-400
  preference: { label: "偏好", color: "#C084FC" },  // purple-400
  task: { label: "任务", color: "#818CF8" },        // indigo-400
  relation: { label: "关系", color: "#E879F9" },    // fuchsia-400
  knowledge: { label: "知识", color: "#A855F7" },   // purple-500
};

/** 记忆分层 → 中文标签 + 描述 */
const SCOPE_META: Record<MemoryScope, { label: string; desc: string; color: string }> = {
  short: { label: "短期", desc: "会话内", color: "#60A5FA" },     // blue
  mid: { label: "中期", desc: "最近 7 天", color: "#F59E0B" },    // amber
  long: { label: "长期", desc: "永久", color: "#10B981" },        // emerald
};

/** 类型筛选项 */
const TYPE_FILTERS: { value: MemoryType | "all"; label: string }[] = [
  { value: "all", label: "全部类型" },
  { value: "fact", label: "事实" },
  { value: "preference", label: "偏好" },
  { value: "task", label: "任务" },
  { value: "relation", label: "关系" },
  { value: "knowledge", label: "知识" },
];

/** 分层筛选项 */
const SCOPE_FILTERS: { value: MemoryScope | "all"; label: string }[] = [
  { value: "all", label: "全部分层" },
  { value: "short", label: "短期" },
  { value: "mid", label: "中期" },
  { value: "long", label: "长期" },
];

// ============================================
// 工具函数
// ============================================

/** 生成唯一 ID（优先 crypto.randomUUID，降级到时间戳+随机数） */
function genId(): string {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return crypto.randomUUID();
    }
  } catch {
    /* ignore */
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** 格式化时间（简洁显示） */
function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString("zh-CN", {
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso.slice(0, 16).replace("T", " ");
  }
}

/** 标签字符串 → 标签数组（去重、去空、转小写） */
function parseTags(input: string): string[] {
  return Array.from(
    new Set(
      input
        .split(/[,，;；\s]+/)
        .map((s) => s.trim())
        .filter(Boolean)
    )
  );
}

// ============================================
// localStorage 读写
// ============================================

function loadMemories(): MemoryItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // 简单校验每条记录的必要字段
    return parsed.filter(
      (m): m is MemoryItem =>
        m &&
        typeof m.id === "string" &&
        typeof m.title === "string" &&
        typeof m.content === "string"
    );
  } catch (err) {
    console.warn("[HermesMemoryPanel] 读取 localStorage 失败:", err);
    return [];
  }
}

function saveMemories(items: MemoryItem[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (err) {
    console.warn("[HermesMemoryPanel] 写入 localStorage 失败:", err);
  }
}

// ============================================
// 主组件
// ============================================

export default function HermesMemoryPanel() {
  // 记忆列表
  const [memories, setMemories] = useState<MemoryItem[]>([]);
  // 搜索关键词
  const [keyword, setKeyword] = useState("");
  // 类型筛选
  const [typeFilter, setTypeFilter] = useState<MemoryType | "all">("all");
  // 分层筛选
  const [scopeFilter, setScopeFilter] = useState<MemoryScope | "all">("all");

  // 添加 / 编辑 弹窗
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<MemoryForm>(emptyForm());

  // 知识关联弹窗
  const [knowledgeModal, setKnowledgeModal] = useState<{
    open: boolean;
    loading: boolean;
    memoryTitle: string;
    results: KnowledgeResult[];
    error: string;
  }>({ open: false, loading: false, memoryTitle: "", results: [], error: "" });

  // ============================================
  // 初始化：从 localStorage 读取
  // ============================================
  useEffect(() => {
    setMemories(loadMemories());
  }, []);

  // ============================================
  // 持久化：memories 变化时写回 localStorage
  // ============================================
  useEffect(() => {
    saveMemories(memories);
  }, [memories]);

  // ============================================
  // 统计：总数 / 各类型分布 / 各分层分布
  // ============================================
  const stats = useMemo(() => {
    const total = memories.length;
    const byType: Record<MemoryType, number> = {
      fact: 0,
      preference: 0,
      task: 0,
      relation: 0,
      knowledge: 0,
    };
    const byScope: Record<MemoryScope, number> = { short: 0, mid: 0, long: 0 };
    for (const m of memories) {
      byType[m.type]++;
      byScope[m.scope]++;
    }
    return { total, byType, byScope };
  }, [memories]);

  // ============================================
  // 过滤 + 排序：置顶优先 → 更新时间倒序
  // ============================================
  const filteredMemories = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return memories
      .filter((m) => {
        // 类型筛选
        if (typeFilter !== "all" && m.type !== typeFilter) return false;
        // 分层筛选
        if (scopeFilter !== "all" && m.scope !== scopeFilter) return false;
        // 关键词匹配：标题 / 内容 / 标签
        if (kw) {
          const hit =
            m.title.toLowerCase().includes(kw) ||
            m.content.toLowerCase().includes(kw) ||
            m.tags.some((t) => t.toLowerCase().includes(kw));
          if (!hit) return false;
        }
        return true;
      })
      .sort((a, b) => {
        // 置顶优先
        if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
        // 更新时间倒序
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      });
  }, [memories, keyword, typeFilter, scopeFilter]);

  // ============================================
  // 打开"添加"弹窗
  // ============================================
  const openAddModal = useCallback(() => {
    setEditingId(null);
    setForm(emptyForm());
    setModalOpen(true);
  }, []);

  // ============================================
  // 打开"编辑"弹窗
  // ============================================
  const openEditModal = useCallback((m: MemoryItem) => {
    setEditingId(m.id);
    setForm({
      type: m.type,
      scope: m.scope,
      title: m.title,
      content: m.content,
      tagsInput: m.tags.join(", "),
    });
    setModalOpen(true);
  }, []);

  // ============================================
  // 提交表单（添加 / 编辑）
  // ============================================
  const submitForm = useCallback(() => {
    const title = form.title.trim();
    const content = form.content.trim();
    if (!title || !content) return;

    const now = new Date().toISOString();
    const tags = parseTags(form.tagsInput);

    if (editingId) {
      // 编辑：保留 createdAt / accessCount / source / pinned
      setMemories((prev) =>
        prev.map((m) =>
          m.id === editingId
            ? {
                ...m,
                type: form.type,
                scope: form.scope,
                title,
                content,
                tags,
                updatedAt: now,
              }
            : m
        )
      );
    } else {
      // 添加
      const item: MemoryItem = {
        id: genId(),
        type: form.type,
        scope: form.scope,
        title,
        content,
        tags,
        pinned: false,
        createdAt: now,
        updatedAt: now,
        accessCount: 0,
        source: "manual",
      };
      setMemories((prev) => [item, ...prev]);
    }

    setModalOpen(false);
    setEditingId(null);
    setForm(emptyForm());
  }, [form, editingId]);

  // ============================================
  // 删除记忆
  // ============================================
  const deleteMemory = useCallback((id: string) => {
    setMemories((prev) => prev.filter((m) => m.id !== id));
  }, []);

  // ============================================
  // 切换置顶
  // ============================================
  const togglePin = useCallback((id: string) => {
    setMemories((prev) =>
      prev.map((m) =>
        m.id === id
          ? { ...m, pinned: !m.pinned, updatedAt: new Date().toISOString() }
          : m
      )
    );
  }, []);

  // ============================================
  // 知识关联：调用 HermesRuntime 检索企业知识库
  // ============================================
  const waitForRuntime = useCallback(
    (): Promise<boolean> => RuntimeManager.waitForRuntime({ timeoutMs: 6000, intervalMs: 300 }),
    []
  );

  const searchRelatedKnowledge = useCallback(
    async (memory: MemoryItem) => {
      setKnowledgeModal({
        open: true,
        loading: true,
        memoryTitle: memory.title,
        results: [],
        error: "",
      });

      // 用标题 + 内容片段作为检索 query
      const query = memory.title + " " + memory.content.slice(0, 80);

      const ready = await waitForRuntime();
      if (!ready) {
        setKnowledgeModal({
          open: true,
          loading: false,
          memoryTitle: memory.title,
          results: [],
          error: "RuntimeManager 尚未就绪，请稍后重试",
        });
        return;
      }

      try {
        const resp = await RuntimeManager.query(RuntimeName.Hermes, {
          type: "memory_search",
          query,
        });

        // 无返回 / 失败 → 空状态
        if (!resp?.success || !resp.data) {
          setKnowledgeModal({
            open: true,
            loading: false,
            memoryTitle: memory.title,
            results: [],
            error: resp?.error || "",
          });
          return;
        }

        // 宽松解析多种可能的返回结构
        const results = extractKnowledgeResults(resp.data);
        setKnowledgeModal({
          open: true,
          loading: false,
          memoryTitle: memory.title,
          results,
          error: "",
        });

        // 记录访问
        setMemories((prev) =>
          prev.map((m) =>
            m.id === memory.id
              ? {
                  ...m,
                  lastAccessedAt: new Date().toISOString(),
                  accessCount: m.accessCount + 1,
                }
              : m
          )
        );
      } catch (err) {
        setKnowledgeModal({
          open: true,
          loading: false,
          memoryTitle: memory.title,
          results: [],
          error: err instanceof Error ? err.message : String(err),
        });
      }
    },
    [waitForRuntime]
  );

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative h-full flex flex-col rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带（紫色主色调） */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-violet-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-violet-500/15 border border-violet-500/30">
            <Brain size={13} className="text-violet-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">企业记忆系统</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Hermes Memory · 持续累积 · 自动关联企业知识
            </p>
          </div>
        </div>
        <span className="flex items-center gap-1 px-2 py-0.5 rounded-md border border-violet-500/20 bg-violet-500/5 text-[9px] font-mono text-violet-400 uppercase tracking-wider">
          <Sparkles size={9} />
          P0 · Hermes
        </span>
      </div>

      {/* 顶部统计卡片 */}
      <div className="p-3 border-b border-white/[0.04] space-y-2">
        {/* 总数 + 分层分布 */}
        <div className="grid grid-cols-4 gap-2">
          <StatCard
            label="总记忆数"
            value={stats.total}
            icon={<Database size={11} />}
            color="#A855F7"
          />
          <StatCard
            label="短期 · 会话内"
            value={stats.byScope.short}
            icon={<Clock size={11} />}
            color={SCOPE_META.short.color}
          />
          <StatCard
            label="中期 · 7 天"
            value={stats.byScope.mid}
            icon={<Clock size={11} />}
            color={SCOPE_META.mid.color}
          />
          <StatCard
            label="长期 · 永久"
            value={stats.byScope.long}
            icon={<Clock size={11} />}
            color={SCOPE_META.long.color}
          />
        </div>

        {/* 类型分布 */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="flex items-center gap-1 text-[9px] font-mono text-gray-500 uppercase tracking-wider mr-1">
            <Layers size={9} />
            类型分布
          </span>
          {(Object.keys(TYPE_META) as MemoryType[]).map((t) => (
            <span
              key={t}
              className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono border"
              style={{
                color: TYPE_META[t].color,
                borderColor: `${TYPE_META[t].color}33`,
                background: `${TYPE_META[t].color}0d`,
              }}
            >
              {TYPE_META[t].label}
              <span className="font-bold">{stats.byType[t]}</span>
            </span>
          ))}
        </div>
      </div>

      {/* 搜索栏 + 筛选器 + 添加按钮 */}
      <div className="p-3 border-b border-white/[0.04] space-y-2">
        <div className="flex items-center gap-2">
          {/* 搜索框 */}
          <div className="flex-1 flex items-center gap-2 rounded-lg border border-white/[0.06] bg-white/[0.02] px-2.5 py-1.5 focus-within:border-violet-500/40 transition-colors">
            <Search size={12} className="text-violet-400/70 flex-shrink-0" />
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索记忆标题 / 内容 / 标签..."
              className="flex-1 bg-transparent text-[12px] text-gray-200 placeholder-gray-600 outline-none font-mono"
            />
            {keyword && (
              <button
                onClick={() => setKeyword("")}
                className="text-gray-600 hover:text-gray-400 transition-colors"
                title="清除"
              >
                <X size={11} />
              </button>
            )}
          </div>
          {/* 添加按钮 */}
          <button
            onClick={openAddModal}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-violet-500/15 border border-violet-500/30 hover:bg-violet-500/25 transition-colors flex-shrink-0"
            title="添加记忆"
          >
            <Plus size={12} className="text-violet-400" />
            <span className="text-[10px] font-mono text-violet-300 uppercase tracking-wider">添加</span>
          </button>
        </div>

        {/* 筛选器行 */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="flex items-center gap-1 text-[9px] font-mono text-gray-500 uppercase tracking-wider">
            <Filter size={9} />
            筛选
          </span>
          {/* 类型筛选 */}
          <div className="flex items-center gap-1 flex-wrap">
            {TYPE_FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => setTypeFilter(f.value)}
                className={`px-1.5 py-0.5 rounded text-[9px] font-mono border transition-colors ${
                  typeFilter === f.value
                    ? "border-violet-500/40 bg-violet-500/15 text-violet-300"
                    : "border-white/[0.06] bg-white/[0.02] text-gray-500 hover:text-gray-300"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <span className="w-px h-3 bg-white/[0.08] mx-1" />
          {/* 分层筛选 */}
          <div className="flex items-center gap-1 flex-wrap">
            {SCOPE_FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => setScopeFilter(f.value)}
                className={`px-1.5 py-0.5 rounded text-[9px] font-mono border transition-colors ${
                  scopeFilter === f.value
                    ? "border-violet-500/40 bg-violet-500/15 text-violet-300"
                    : "border-white/[0.06] bg-white/[0.02] text-gray-500 hover:text-gray-300"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 记忆列表 */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {filteredMemories.length === 0 ? (
          // 空状态
          <EmptyState
            hasMemories={memories.length > 0}
            onAdd={openAddModal}
          />
        ) : (
          filteredMemories.map((m) => (
            <MemoryCard
              key={m.id}
              memory={m}
              onEdit={() => openEditModal(m)}
              onDelete={() => deleteMemory(m.id)}
              onTogglePin={() => togglePin(m.id)}
              onSearchKnowledge={() => void searchRelatedKnowledge(m)}
            />
          ))
        )}
      </div>

      {/* 底部数据来源说明 */}
      <div className="px-3 py-1.5 border-t border-white/[0.04] flex items-center justify-between">
        <span className="text-[8px] font-mono text-gray-600">
          数据来源：localStorage({STORAGE_KEY}) · 知识关联：HermesRuntime · memory_search
        </span>
        <span className="text-[8px] font-mono text-gray-700">
          {filteredMemories.length} / {memories.length} 条
        </span>
      </div>

      {/* 添加 / 编辑 弹窗 */}
      <AnimatePresence>
        {modalOpen && (
          <MemoryFormModal
            editing={editingId !== null}
            form={form}
            onChange={setForm}
            onSubmit={submitForm}
            onClose={() => {
              setModalOpen(false);
              setEditingId(null);
            }}
          />
        )}
      </AnimatePresence>

      {/* 知识关联弹窗 */}
      <AnimatePresence>
        {knowledgeModal.open && (
          <KnowledgeModal
            state={knowledgeModal}
            onClose={() =>
              setKnowledgeModal({
                open: false,
                loading: false,
                memoryTitle: "",
                results: [],
                error: "",
              })
            }
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================================
// 子组件：统计卡片
// ============================================

interface StatCardProps {
  label: string;
  value: number;
  icon: React.ReactNode;
  color: string;
}

function StatCard({ label, value, icon, color }: StatCardProps) {
  return (
    <div className="relative rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5 overflow-hidden">
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)` }}
      />
      <div className="flex items-center gap-1 mb-1" style={{ color }}>
        {icon}
        <span className="text-[8px] font-mono uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-[18px] font-mono font-bold" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

// ============================================
// 子组件：空状态
// ============================================

interface EmptyStateProps {
  hasMemories: boolean;
  onAdd: () => void;
}

function EmptyState({ hasMemories, onAdd }: EmptyStateProps) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center py-10">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="w-14 h-14 rounded-xl flex items-center justify-center bg-violet-500/10 border border-violet-500/20 mb-3"
      >
        <Inbox size={24} className="text-violet-400/70" />
      </motion.div>
      <h4 className="text-[13px] font-semibold text-gray-300 mb-1">
        {hasMemories ? "未匹配到记忆" : "暂无企业记忆"}
      </h4>
      <p className="text-[10px] font-mono text-gray-500 mb-3">
        {hasMemories
          ? "尝试调整筛选条件或更换关键词"
          : "Hermes 将持续累积企业上下文，从零到不再遗忘"}
      </p>
      {!hasMemories && (
        <button
          onClick={onAdd}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-violet-500/15 border border-violet-500/30 hover:bg-violet-500/25 transition-colors"
        >
          <Plus size={12} className="text-violet-400" />
          <span className="text-[10px] font-mono text-violet-300 uppercase tracking-wider">
            点击添加
          </span>
        </button>
      )}
    </div>
  );
}

// ============================================
// 子组件：记忆卡片
// ============================================

interface MemoryCardProps {
  memory: MemoryItem;
  onEdit: () => void;
  onDelete: () => void;
  onTogglePin: () => void;
  onSearchKnowledge: () => void;
}

function MemoryCard({
  memory,
  onEdit,
  onDelete,
  onTogglePin,
  onSearchKnowledge,
}: MemoryCardProps) {
  const typeMeta = TYPE_META[memory.type];
  const scopeMeta = SCOPE_META[memory.scope];

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className={`relative rounded-lg border p-2.5 group transition-colors ${
        memory.pinned
          ? "border-violet-500/30 bg-violet-500/[0.04]"
          : "border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]"
      }`}
    >
      {/* 置顶左侧色条 */}
      {memory.pinned && (
        <div className="absolute left-0 top-2 bottom-2 w-0.5 rounded-full bg-violet-500/60" />
      )}

      {/* 卡片头部：标题 + 类型/分层徽章 */}
      <div className="flex items-start gap-2 mb-1.5">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap mb-1">
            {/* 类型徽章 */}
            <span
              className="px-1.5 py-0.5 rounded text-[8px] font-mono border"
              style={{
                color: typeMeta.color,
                borderColor: `${typeMeta.color}33`,
                background: `${typeMeta.color}0d`,
              }}
            >
              {typeMeta.label}
            </span>
            {/* 分层徽章 */}
            <span
              className="px-1.5 py-0.5 rounded text-[8px] font-mono border"
              style={{
                color: scopeMeta.color,
                borderColor: `${scopeMeta.color}33`,
                background: `${scopeMeta.color}0d`,
              }}
              title={scopeMeta.desc}
            >
              {scopeMeta.label} · {scopeMeta.desc}
            </span>
            {/* 来源徽章 */}
            <span className="px-1 py-0.5 rounded text-[8px] font-mono border border-white/[0.06] text-gray-500">
              {memory.source === "auto" ? "自动提取" : "人工录入"}
            </span>
            {memory.pinned && (
              <span className="flex items-center gap-0.5 px-1 py-0.5 rounded text-[8px] font-mono border border-violet-500/30 text-violet-400 bg-violet-500/10">
                <Pin size={8} />
                置顶
              </span>
            )}
          </div>
          <h4 className="text-[12px] font-semibold text-gray-200 leading-snug">
            {memory.title}
          </h4>
        </div>
      </div>

      {/* 内容预览 */}
      <p className="text-[11px] text-gray-400 leading-relaxed mb-2 line-clamp-3 whitespace-pre-wrap break-words">
        {memory.content}
      </p>

      {/* 标签 */}
      {memory.tags.length > 0 && (
        <div className="flex items-center gap-1 flex-wrap mb-2">
          {memory.tags.map((tag) => (
            <span
              key={tag}
              className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] font-mono bg-violet-500/[0.06] border border-violet-500/20 text-violet-300/80"
            >
              <Tag size={7} />
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* 卡片底部：时间 + 操作按钮 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[8px] font-mono text-gray-600">
          <span title="更新时间">
            更新 {formatTime(memory.updatedAt)}
          </span>
          {memory.accessCount > 0 && (
            <span title="访问次数">访问 {memory.accessCount} 次</span>
          )}
        </div>
        <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
          {/* 知识关联 */}
          <button
            onClick={onSearchKnowledge}
            className="p-1 rounded text-violet-400/70 hover:text-violet-300 hover:bg-violet-500/10 transition-colors"
            title="关联企业知识"
          >
            <Link2 size={11} />
          </button>
          {/* 置顶 / 取消置顶 */}
          <button
            onClick={onTogglePin}
            className={`p-1 rounded transition-colors ${
              memory.pinned
                ? "text-violet-400 hover:bg-violet-500/10"
                : "text-gray-500 hover:text-violet-300 hover:bg-violet-500/10"
            }`}
            title={memory.pinned ? "取消置顶" : "置顶"}
          >
            {memory.pinned ? <PinOff size={11} /> : <Pin size={11} />}
          </button>
          {/* 编辑 */}
          <button
            onClick={onEdit}
            className="p-1 rounded text-gray-500 hover:text-cyan-300 hover:bg-cyan-500/10 transition-colors"
            title="编辑"
          >
            <Pencil size={11} />
          </button>
          {/* 删除 */}
          <button
            onClick={onDelete}
            className="p-1 rounded text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
            title="删除"
          >
            <Trash2 size={11} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// ============================================
// 子组件：添加 / 编辑 表单弹窗
// ============================================

interface MemoryFormModalProps {
  editing: boolean;
  form: MemoryForm;
  onChange: (f: MemoryForm) => void;
  onSubmit: () => void;
  onClose: () => void;
}

function MemoryFormModal({
  editing,
  form,
  onChange,
  onSubmit,
  onClose,
}: MemoryFormModalProps) {
  const canSubmit = form.title.trim() && form.content.trim();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-8"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, y: 10 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 10 }}
        transition={{ duration: 0.2 }}
        className="relative w-full max-w-xl rounded-xl border border-violet-500/30 bg-[#0a0e1a] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 顶部光带 */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-violet-500/60 to-transparent" />

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded flex items-center justify-center bg-violet-500/15 border border-violet-500/30">
              <Brain size={11} className="text-violet-400" />
            </div>
            <h3 className="text-[13px] font-semibold text-white">
              {editing ? "编辑记忆" : "添加企业记忆"}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.05] transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* 表单内容 */}
        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          {/* 类型 + 分层 */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">
                记忆类型
              </label>
              <select
                value={form.type}
                onChange={(e) => onChange({ ...form, type: e.target.value as MemoryType })}
                className="w-full px-2 py-1.5 rounded-md bg-white/[0.04] border border-white/[0.08] text-[12px] text-gray-200 font-mono outline-none focus:border-violet-500/40"
              >
                {(Object.keys(TYPE_META) as MemoryType[]).map((t) => (
                  <option key={t} value={t} className="bg-[#0a0e1a]">
                    {TYPE_META[t].label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">
                记忆分层
              </label>
              <select
                value={form.scope}
                onChange={(e) => onChange({ ...form, scope: e.target.value as MemoryScope })}
                className="w-full px-2 py-1.5 rounded-md bg-white/[0.04] border border-white/[0.08] text-[12px] text-gray-200 font-mono outline-none focus:border-violet-500/40"
              >
                {(Object.keys(SCOPE_META) as MemoryScope[]).map((s) => (
                  <option key={s} value={s} className="bg-[#0a0e1a]">
                    {SCOPE_META[s].label} · {SCOPE_META[s].desc}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* 标题 */}
          <div>
            <label className="block text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">
              标题 <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => onChange({ ...form, title: e.target.value })}
              placeholder="例如：公司核心业务方向"
              className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.04] border border-white/[0.08] text-[12px] text-gray-200 placeholder-gray-600 font-mono outline-none focus:border-violet-500/40"
            />
          </div>

          {/* 内容 */}
          <div>
            <label className="block text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">
              内容 <span className="text-red-400">*</span>
            </label>
            <textarea
              value={form.content}
              onChange={(e) => onChange({ ...form, content: e.target.value })}
              placeholder="详细描述这条记忆的内容..."
              rows={4}
              className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.04] border border-white/[0.08] text-[12px] text-gray-200 placeholder-gray-600 font-mono outline-none focus:border-violet-500/40 resize-none leading-relaxed"
            />
          </div>

          {/* 标签 */}
          <div>
            <label className="block text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">
              标签 <span className="text-gray-600">（逗号分隔）</span>
            </label>
            <input
              type="text"
              value={form.tagsInput}
              onChange={(e) => onChange({ ...form, tagsInput: e.target.value })}
              placeholder="例如：业务, 团队, 目标"
              className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.04] border border-white/[0.08] text-[12px] text-gray-200 placeholder-gray-600 font-mono outline-none focus:border-violet-500/40"
            />
          </div>
        </div>

        {/* 底部操作 */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-white/[0.06] bg-white/[0.01]">
          <span className="text-[9px] font-mono text-gray-600">
            数据持久化：localStorage({STORAGE_KEY})
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 rounded-md text-[11px] font-mono text-gray-400 hover:text-white border border-white/[0.08] hover:bg-white/[0.05] transition-colors"
            >
              取消
            </button>
            <button
              onClick={onSubmit}
              disabled={!canSubmit}
              className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[11px] font-mono text-violet-300 bg-violet-500/15 border border-violet-500/30 hover:bg-violet-500/25 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <Plus size={11} />
              {editing ? "保存" : "添加"}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// 子组件：知识关联结果弹窗
// ============================================

interface KnowledgeModalProps {
  state: {
    open: boolean;
    loading: boolean;
    memoryTitle: string;
    results: KnowledgeResult[];
    error: string;
  };
  onClose: () => void;
}

function KnowledgeModal({ state, onClose }: KnowledgeModalProps) {
  const { loading, memoryTitle, results, error } = state;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-8"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, y: 10 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 10 }}
        transition={{ duration: 0.2 }}
        className="relative w-full max-w-2xl max-h-[80vh] flex flex-col rounded-xl border border-violet-500/30 bg-[#0a0e1a] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 顶部光带 */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-violet-500/60 to-transparent" />

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-6 h-6 rounded flex items-center justify-center bg-violet-500/15 border border-violet-500/30 flex-shrink-0">
              <Link2 size={11} className="text-violet-400" />
            </div>
            <div className="min-w-0">
              <h3 className="text-[13px] font-semibold text-white truncate">
                关联企业知识
              </h3>
              <p className="text-[9px] font-mono text-gray-500 truncate">
                来源记忆：{memoryTitle}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.05] transition-colors flex-shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        {/* 内容区 */}
        <div className="flex-1 min-h-0 overflow-y-auto p-5">
          {loading ? (
            // 加载中
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <Loader2 size={18} className="text-violet-400 animate-spin mb-2" />
              <span className="text-[11px] font-mono">正在检索企业知识库...</span>
              <span className="text-[9px] font-mono text-gray-600 mt-1">
                HermesRuntime · memory_search
              </span>
            </div>
          ) : error && results.length === 0 ? (
            // 检索出错
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <AlertCircle size={20} className="text-amber-400/70 mb-2" />
              <span className="text-[11px] font-mono text-amber-400 mb-1">
                检索异常
              </span>
              <span className="text-[10px] font-mono text-gray-500 max-w-md break-words">
                {error}
              </span>
            </div>
          ) : results.length === 0 ? (
            // 无关联知识（空状态）
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <Inbox size={20} className="text-gray-600 mb-2" />
              <span className="text-[11px] font-mono text-gray-400 mb-1">
                暂无关联知识
              </span>
              <span className="text-[10px] font-mono text-gray-600 max-w-md">
                企业知识库中未检索到与该记忆相关的内容。
                可在知识中心补充相关知识后再次检索。
              </span>
            </div>
          ) : (
            // 检索结果列表
            <div className="space-y-2">
              <div className="flex items-center gap-1 mb-1">
                <Sparkles size={10} className="text-violet-400/70" />
                <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                  检索到 {results.length} 条关联知识
                </span>
              </div>
              {results.map((r, idx) => (
                <div
                  key={idx}
                  className="p-2.5 rounded-lg border border-white/[0.06] bg-white/[0.02] hover:bg-violet-500/[0.04] hover:border-violet-500/30 transition-colors"
                >
                  <div className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-4 h-4 rounded flex items-center justify-center bg-violet-500/15 text-violet-400 text-[9px] font-mono font-bold mt-0.5">
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <FileText size={10} className="text-violet-400/70 flex-shrink-0" />
                        <span className="text-[11px] text-gray-200 truncate">
                          {r.title || r.source || "未命名知识"}
                        </span>
                        {typeof r.score === "number" && (
                          <span className="text-[8px] font-mono text-violet-400/60 flex-shrink-0">
                            相关度 {(r.score * 100).toFixed(0)}%
                          </span>
                        )}
                      </div>
                      <p className="text-[10px] text-gray-500 leading-snug line-clamp-3">
                        {r.snippet || r.content || "无内容预览"}
                      </p>
                      {r.path && (
                        <p className="text-[8px] font-mono text-gray-700 mt-1 truncate" title={r.path}>
                          📁 {r.path}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 底部来源说明 */}
        <div className="px-5 py-2 border-t border-white/[0.06] bg-white/[0.01]">
          <span className="text-[9px] font-mono text-gray-600">
            数据来源：RuntimeManager.query(RuntimeName.Hermes, {`{ type: 'memory_search', query }`})
          </span>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// 辅助：表单初始值
// ============================================

function emptyForm(): MemoryForm {
  return {
    type: "fact",
    scope: "long",
    title: "",
    content: "",
    tagsInput: "",
  };
}

// ============================================
// 辅助：宽松解析 HermesRuntime memory_search 返回结果
// ============================================

function extractKnowledgeResults(data: unknown): KnowledgeResult[] {
  if (!data) return [];

  // 情况 1：直接是数组
  if (Array.isArray(data)) {
    return data.map(normalizeKnowledgeItem).filter(Boolean) as KnowledgeResult[];
  }

  // 情况 2：包了一层 { success, data }
  const obj = data as Record<string, any>;
  if (obj && typeof obj === "object") {
    const inner = obj.data ?? obj.results ?? obj.items ?? obj.knowledge ?? obj.records;
    if (Array.isArray(inner)) {
      return inner.map(normalizeKnowledgeItem).filter(Boolean) as KnowledgeResult[];
    }
    // 情况 3：单个对象
    const single = normalizeKnowledgeItem(obj);
    if (single) return [single];
  }

  return [];
}

/** 将任意结构的项归一化为 KnowledgeResult */
function normalizeKnowledgeItem(item: any): KnowledgeResult | null {
  if (!item || typeof item !== "object") return null;
  const title = item.title || item.name || item.fileName || item.subject || undefined;
  const content = item.content || item.body || item.text || undefined;
  const snippet =
    item.snippet || item.summary || item.abstract || item.excerpt || undefined;
  const path = item.path || item.filePath || item.url || item.source || undefined;
  const score = typeof item.score === "number" ? item.score : undefined;
  const source = item.source || item.type || undefined;

  // 至少要有标题或内容才算有效
  if (!title && !content && !snippet) return null;

  return { title, content, snippet, path, score, source };
}
